import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { fetchCategories, fetchListings } from '../lib/api';
import { categoryInfo } from '../data/categoryFaqs';
import { applySeo, resetSeo } from '../lib/seo';
import { slugifyRegion } from '../lib/regions';
import ListingCard from '../components/ListingCard';
import JsonLd from '../components/JsonLd';
import type { Category, Listing } from '../types';

const BASE = 'https://marinelink.co.nz';

export default function CategoryLanding() {
  const { slug } = useParams<{ slug: string }>();
  const [category, setCategory] = useState<Category | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const info = slug ? categoryInfo[slug] : undefined;

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchCategories().then(async (cats) => {
      const cat = cats.find((c) => c.slug === slug);
      if (!cat) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setCategory(cat);
      const items = await fetchListings({ category: cat.id, sort: 'rating' });
      setListings(items);
      setRegions([...new Set(items.map((l) => l.region_name).filter(Boolean))].sort() as string[]);
      setLoading(false);
    });
  }, [slug]);

  useEffect(() => {
    if (category) {
      applySeo({
        title: `${category.name} in New Zealand | MarineLink`,
        description: info
          ? `${info.description} Browse ${listings.length || ''} ${category.name.toLowerCase()} listings across NZ.`
          : `Find ${category.name.toLowerCase()} across New Zealand on MarineLink, the NZ marine industry directory.`,
        descriptionPad: `Compare ratings, services and contact details, and connect directly with ${category.name.toLowerCase()} businesses across every NZ region.`,
        path: `/category/${category.slug}`,
        keywords: `${category.name} NZ, ${category.name} New Zealand, marine directory, MarineLink`,
      });
    }
    return () => resetSeo();
  }, [category, info, listings.length]);

  if (notFound) return <Navigate to="/categories" replace />;

  if (loading || !category) {
    return <div className="text-center py-24 text-text-muted-light dark:text-text-muted">Loading...</div>;
  }

  const faqSchema = info && info.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${BASE}/category/${category.slug}#faq`,
    mainEntity: info.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${category.name} in New Zealand`,
    numberOfItems: listings.length,
    itemListElement: listings.slice(0, 25).map((l, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: l.title,
      url: `${BASE}/listing/${l.category_slug || category.slug}/${l.slug}`,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE}/` },
      { '@type': 'ListItem', position: 2, name: 'Categories', item: `${BASE}/categories` },
      { '@type': 'ListItem', position: 3, name: category.name, item: `${BASE}/category/${category.slug}` },
    ],
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-8 py-8 md:py-12">
      <JsonLd id={`cat-${category.slug}`} data={[breadcrumbSchema, itemListSchema, ...(faqSchema ? [faqSchema] : [])]} />

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-muted-light dark:text-text-muted mb-6 flex-wrap">
        <Link to="/" className="text-text-secondary-light dark:text-text-secondary hover:text-accent transition-colors">Home</Link>
        <span>/</span>
        <Link to="/categories" className="text-text-secondary-light dark:text-text-secondary hover:text-accent transition-colors">Categories</Link>
        <span>/</span>
        <span className="text-text-primary-light dark:text-text-primary">{category.name}</span>
      </div>

      {/* Hero */}
      <div className="mb-10 max-w-3xl">
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-text-primary-light dark:text-text-primary mb-4">
          {category.name} <span className="text-text-muted-light dark:text-text-muted font-medium">in New Zealand</span>
        </h1>
        {info && <p className="text-text-secondary-light dark:text-text-secondary text-lg leading-relaxed">{info.description}</p>}
        <p className="text-text-muted-light dark:text-text-muted mt-3 text-sm">
          {listings.length} listing{listings.length === 1 ? '' : 's'} nationwide
          {' · '}
          <Link to={`/browse?category=${category.id}`} className="text-accent hover:underline">Filter by region</Link>
        </p>
      </div>

      {/* Browse by region */}
      {regions.length > 0 && (
        <nav className="mb-10" aria-label="Browse by region">
          <div className="flex flex-wrap gap-2">
            {regions.map((r) => (
              <Link
                key={r}
                to={`/category/${category.slug}/${slugifyRegion(r)}`}
                className="px-4 py-2 rounded-full border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] text-sm text-text-secondary-light dark:text-text-secondary hover:border-accent hover:text-accent transition-colors"
              >
                {category.name} in {r}
              </Link>
            ))}
          </div>
        </nav>
      )}

      {/* Listings */}
      {listings.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-16">
          {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      ) : (
        <div className="text-text-muted-light dark:text-text-muted mb-16">No listings in this category yet.</div>
      )}

      {/* FAQ */}
      {info && info.faqs.length > 0 && (
        <section className="max-w-3xl" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="font-display text-2xl md:text-3xl font-bold text-text-primary-light dark:text-text-primary mb-2">
            {category.name}: frequently asked questions
          </h2>
          <p className="text-text-muted-light dark:text-text-muted mb-6 text-sm">
            Expert answers about {category.name.toLowerCase()} in New Zealand. More in the <Link to="/faq" className="text-accent hover:underline">marine industry FAQ hub</Link>.
          </p>
          <div className="space-y-3">
            {info.faqs.map((f) => (
              <details key={f.q} className="group border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] rounded-lg bg-surface-light dark:bg-surface overflow-hidden">
                <summary className="cursor-pointer list-none px-5 py-4 font-medium text-text-primary-light dark:text-text-primary flex items-center justify-between gap-4 hover:text-accent transition-colors">
                  <span>{f.q}</span>
                  <span className="text-text-muted-light dark:text-text-muted group-open:rotate-45 transition-transform text-xl leading-none shrink-0">+</span>
                </summary>
                <div className="px-5 pb-5 text-text-secondary-light dark:text-text-secondary leading-relaxed border-t border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] pt-4">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
